/**
 * useCaseSearchDropdown
 *
 * Standalone loadOptions function for the case search paginated dropdown,
 * designed for use with react-select-async-paginate.
 *
 * Calls POST /search-cases-by-title (OpenSearch, fuzzy-matched by case name/title).
 *
 * Pagination strategy: page-based
 *   Request  → `page` (1-based integer) in request body
 *   Response → `pagination.has_next` determines whether more pages exist
 *
 * The `additional` object carries the next page number between loads: { page: number }
 *
 * Search behaviour:
 *   The `search` argument from react-select-async-paginate is passed directly
 *   as the `query` body field. An empty search string sends a wildcard-style
 *   query so the initial open still populates results.
 *
 * @module useCaseSearchDropdown
 */

import { searchCasesByTitle } from 'services/case/caseServices';

const DEFAULT_PAGE_SIZE = 15;

/**
 * Converts a raw search-cases-by-title result item into a react-select option shape.
 *
 * Preserved fields allow consumers to read any API field directly from the
 * selected option object without an additional lookup.
 *
 * @param {Object}  item
 * @param {string}  item.case_id      - Case or lead ID
 * @param {string}  item.case_name    - Primary label field
 * @param {string}  item.case_number  - Case reference number
 * @param {string}  item.subtitle     - Case subtitle / category
 * @param {string}  item.description  - Case description
 * @param {string}  item.meta         - Additional metadata
 * @param {string}  item.res_type     - "case" | "lead"
 * @param {string}  item.res_type_id  - Resource ID
 * @param {string}  item.tenant_id    - Tenant/firm ID
 * @param {string}  item.pk           - Primary key ({tenant_id}#{case_id})
 * @param {string}  item.sk           - Sort key ({res_type}#{id})
 * @param {boolean} item.is_lead      - true if lead, false if case
 * @param {boolean} item.case_closed  - Whether the case is closed
 * @param {boolean} item.is_deleted   - Deletion flag
 * @param {string}  item.created_at   - ISO 8601 creation timestamp
 * @param {string}  item.updated_at   - ISO 8601 last-update timestamp
 * @param {number}  item._score       - OpenSearch relevance score
 * @returns {{ label: string, value: string, ...item }}
 */
export const toCaseOption = (item) => ({
  label: item.case_name || 'Untitled Case',
  value: item.case_id,
  ...item,
});

/**
 * loadOptions adapter for react-select-async-paginate.
 *
 * Signature expected by AsyncPaginate:
 *   (searchQuery, loadedOptions, additional) => Promise<{ options, hasMore, additional }>
 *
 * @param {string}      search          - Search query typed by the user
 * @param {Array}       _loadedOptions  - Already-loaded options (unused)
 * @param {Object}      additional      - { page: number } cursor carried between page loads
 * @param {Object}      config
 * @param {number}      [config.pageSize]      - Results per page (default: 15)
 * @param {string}      [config.sortBy]        - 'date' | 'relevance' (default: 'relevance')
 * @param {string}      [config.sortOrder]     - 'asc' | 'desc' (default: 'desc')
 * @param {string|null} [config.filterBy]      - 'case' | 'lead' | null for both (default: null)
 * @param {boolean}     [config.includeClosed] - Include closed cases (default: false)
 */
export const loadCaseSearchOptions = async (search, _loadedOptions, additional, config = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, sortBy = 'relevance', sortOrder = 'desc', filterBy = null, includeClosed = false } = config;

  const page = additional?.page || 1;

  try {
    const response = await searchCasesByTitle({
      query: search || '',
      page,
      size: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
      filter_by: filterBy,
      include_closed: includeClosed,
    });

    const items = response?.data?.results || [];
    const pagination = response?.data?.pagination || {};

    return {
      options: items.map(toCaseOption),
      hasMore: pagination.has_next || false,
      additional: { page: page + 1 },
    };
  } catch (error) {
    console.error('Error loading case search options:', error);
    return { options: [], hasMore: false, additional: { page: 1 } };
  }
};

/**
 * Converts raw API result items back into the react-select option shape
 * expected by CaseSelect as its `value` prop.
 *
 * Use this when pre-populating a CaseSelect from saved API data.
 *
 * @param {Array} items - Raw API result items
 * @returns {Array<{ label: string, value: string, ...item }>}
 */
export const toCaseOptions = (items = []) => items.map(toCaseOption);
